import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Input,
  InputGroup,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Row,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import AirHomeLoader from "../../../../components/Common/AirHomeLoader";
import ConfirmationModal from "../../../../components/Common/ConfirmationModal";
import Request from "../../../../helpers/request";
import * as url from "../../../../helpers/url_helper";
import { useSocket } from "../../../../Utils/socket";
import { OPERATE_MQTT } from "../../../../helpers/socketEvent";
import { selectedPremiseRoomsNew } from "../../../../store/slices/APIResponseSlice";

const BINDING_NAME_MAX = 50;
const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");

export interface DeviceBindingProps {
  offcanvasOpen: boolean;
  closeOffcanvas(): void;
  userId: any;
  selectedPremise?: any;
}

export interface SelectedSwitch {
  chipId: string;
  slave: string;
  roomName: string;
  modelType: string;
  switchId: string;
  button: string;
  displayName: string;
  type: number;
}

const getSwitchTypeLabel = (type: number) => {
  switch (type) {
    case 1:
      return "Dimmer";
    case 2:
      return "Fan";
    case 3:
      return "Curtain";
    default:
      return "Switch";
  }
};

const getSwitchTypeStyle = (type: number) => {
  switch (type) {
    case 1:
      return { bg: "#f3e8ff", color: "#7c3aed", icon: "bx-slider" };
    case 2:
      return { bg: "#dbeafe", color: "#2563eb", icon: "bx-wind" };
    case 3:
      return { bg: "#f1f5f9", color: "#64748b", icon: "bx-layer" };
    default:
      return { bg: "#fef9c3", color: "#ca8a04", icon: "bx-bulb" };
  }
};

const switchKey = (slave: string, switchId: string) => `${slave}:${switchId}`;

const formatBindingDate = (value?: string) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
};

function DeviceBinding({
  offcanvasOpen,
  closeOffcanvas,
  userId,
  selectedPremise,
}: DeviceBindingProps) {
  const socket: any = useSocket();
  const dispatch = useDispatch();

  const selectedRoomsNew = useSelector(
    (state: any) => state.APIResponse.selectedRoomsNew,
  );
  const roomListNew = useSelector(
    (state: any) => state.APIResponse.roomListNew,
  );
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [listSearchText, setListSearchText] = useState("");
  const [listDebouncedSearch, setListDebouncedSearch] = useState("");
  const [chipList, setChipList] = useState<any[]>([]);
  const [bindingList, setBindingList] = useState<any[]>([]);
  const [listPage, setListPage] = useState(1);
  const [listPerPage, setListPerPage] = useState(10);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const [recordsFiltered, setRecordsFiltered] = useState(0);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [showCreateCard, setShowCreateCard] = useState(false);
  const [expandedSlaves, setExpandedSlaves] = useState<Set<string>>(new Set());
  const [selectedSwitches, setSelectedSwitches] = useState<SelectedSwitch[]>(
    [],
  );
  const [bindingName, setBindingName] = useState("");
  const [groupId, setGroupId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteBindingData, setDeleteBindingData] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isToggleModalOpen, setToggleModalOpen] = useState(false);
  const [toggleBindingData, setToggleBindingData] = useState<any>(null);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [listReloadKey, setListReloadKey] = useState(0);
  const [togglingGroupId, setTogglingGroupId] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const listDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFetchingRef = useRef(false);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchText.trim());
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchText]);

  useEffect(() => {
    if (listDebounceRef.current) clearTimeout(listDebounceRef.current);
    listDebounceRef.current = setTimeout(() => {
      setListDebouncedSearch(listSearchText.trim());
      setListPage(1);
    }, 400);
    return () => {
      if (listDebounceRef.current) clearTimeout(listDebounceRef.current);
    };
  }, [listSearchText]);

  const handleRoomChecked = (checkedItem: any) => {
    dispatch(
      selectedPremiseRoomsNew(
        selectedRoomsNew.map((item: any, index: number) => {
          if (item.id === checkedItem.id) {
            return { ...item, isChecked: !item.isChecked };
          } else {
            return { ...item };
          }
        }),
      ),
    );
  };
  const handleRoomAllChecked = (bool: boolean) => {
    dispatch(
      selectedPremiseRoomsNew(
        selectedRoomsNew.map((item: any, index: number) => {
          return { ...item, isChecked: bool };
        }),
      ),
    );
  };

  useEffect(() => {
    if (!offcanvasOpen) {
      setShowCreateCard(false);
      setExpandedSlaves(new Set());
      setSelectedSwitches([]);
      setBindingName("");
      setGroupId(null);
      setDeleteModalOpen(false);
      setDeleteBindingData(null);
      setToggleModalOpen(false);
      setToggleBindingData(null);
      setSearchText("");
      setListSearchText("");
      setListPage(1);
    }
  }, [offcanvasOpen]);

  const listTotalPages = Math.max(
    1,
    Math.ceil(recordsFiltered / listPerPage) || 1,
  );
  const listStartRow =
    recordsFiltered === 0 ? 0 : (listPage - 1) * listPerPage + 1;
  const listEndRow = Math.min(listPage * listPerPage, recordsFiltered);

  const reloadBindingList = useCallback(async () => {
    setListLoading(true);
    try {
      const reqObj = {
        userId,
        ...(selectedPremise ? { premiseId: selectedPremise.id } : {}),
        draw: listPage,
        start: (listPage - 1) * listPerPage,
        length: listPerPage,
        search: {
          value: listDebouncedSearch || "",
        },
      };

      const response = await Request.post(
        `${url.BASE_URL}${url.DEVICE_BINDING_LIST}`,
        reqObj,
      );

      const payload = response.data ?? {};
      const rows = Array.isArray(payload.data) ? payload.data : [];
      setBindingList(rows);
      setRecordsTotal(Number(payload.recordsTotal) || 0);
      setRecordsFiltered(Number(payload.recordsFiltered) || rows.length);
    } catch (error: any) {
      console.error("Error fetching device bindings:", error);
      toast.error("Failed to load device bindings");
      setBindingList([]);
      setRecordsTotal(0);
      setRecordsFiltered(0);
      if (error.response?.status === 401) {
        localStorage.removeItem("userInfo");
        localStorage.removeItem("selectedPremiseID");
        localStorage.removeItem("selectedUserID");
        localStorage.removeItem("selectedUserEmail");
        localStorage.removeItem("Authorization");
        localStorage.removeItem("permissions");
        window.location.reload();
      }
    } finally {
      setListLoading(false);
    }
  }, [userId, selectedPremise, listPage, listPerPage, listDebouncedSearch]);

  useEffect(() => {
    if (!offcanvasOpen || showCreateCard) return;

    let cancelled = false;
    reloadBindingList().then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  }, [offcanvasOpen, showCreateCard, listReloadKey, reloadBindingList]);

  useEffect(() => {
    if (!offcanvasOpen || !showCreateCard) return;

    let cancelled = false;

    const fetchChips = async () => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;
      setLoading(true);

      try {
        const reqObj = {
          draw: 1,
          order: [],
          start: 0,
          length: 1000,
          search: {
            value: debouncedSearch || "",
            regex: false,
          },
          userId,
          ...(selectedPremise ? { premiseId: selectedPremise.id } : {}),
          roomId:
            selectedRoomsNew
              ?.filter((item: any) => item.isChecked)
              .map((item: any) => item.id)
              .join(",") || "",
        };

        const response = await Request.post(
          `${url.BASE_URL}${url.GET_CHIPS_LIST}`,
          reqObj,
        );

        if (cancelled) return;
        setChipList(response.data?.data || []);
      } catch (error: any) {
        if (cancelled) return;
        console.error("Error fetching chips:", error);
        toast.error("Failed to load chips");
        setChipList([]);
        if (error.response?.status === 401) {
          localStorage.removeItem("userInfo");
          localStorage.removeItem("selectedPremiseID");
          localStorage.removeItem("selectedUserID");
          localStorage.removeItem("selectedUserEmail");
          localStorage.removeItem("Authorization");
          localStorage.removeItem("permissions");
          window.location.reload();
        }
      } finally {
        if (!cancelled) setLoading(false);
        isFetchingRef.current = false;
      }
    };

    fetchChips();
    return () => {
      cancelled = true;
    };
  }, [
    offcanvasOpen,
    showCreateCard,
    debouncedSearch,
    userId,
    selectedPremise,
    selectedRoomsNew,
  ]);

  const selectedKeys = useMemo(
    () => new Set(selectedSwitches.map((s) => switchKey(s.slave, s.switchId))),
    [selectedSwitches],
  );

  const filteredChips = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return chipList;

    return chipList.filter((chip) => {
      const chipMatch =
        chip.slave?.toLowerCase().includes(q) ||
        chip.roomName?.toLowerCase().includes(q) ||
        chip.modelType?.toLowerCase().includes(q) ||
        chip.name?.toLowerCase().includes(q);

      const switchMatch = (chip.switches || []).some(
        (sw: any) =>
          sw.displayName?.toLowerCase().includes(q) ||
          sw.button?.toLowerCase().includes(q),
      );

      return chipMatch || switchMatch;
    });
  }, [chipList, searchText]);

  const toggleExpand = (slave: string) => {
    setExpandedSlaves((prev) => {
      const next = new Set(prev);
      if (next.has(slave)) next.delete(slave);
      else next.add(slave);
      return next;
    });
  };

  const isSwitchSelected = (slave: string, switchId: string) =>
    selectedKeys.has(switchKey(slave, switchId));

  const addSwitch = (chip: any, sw: any) => {
    // if (sw.is2WayBind) return;
    const key = switchKey(chip.slave, sw._id);
    if (selectedKeys.has(key)) return;

    setSelectedSwitches((prev) => [
      ...prev,
      {
        chipId: chip.id,
        slave: chip.slave,
        roomName: chip.roomName || "-",
        modelType: chip.modelType || "-",
        switchId: sw._id,
        button: sw.button,
        displayName: sw.displayName,
        type: sw.type ?? 0,
      },
    ]);
  };

  const removeSwitch = (slave: string, switchId: string) => {
    const key = switchKey(slave, switchId);
    setSelectedSwitches((prev) =>
      prev.filter((s) => switchKey(s.slave, s.switchId) !== key),
    );
  };

  const clearAllSelected = () => setSelectedSwitches([]);

  const handlePingClick = (row: { slave?: string }) => {
    if (row?.slave) {
      const reqObj = {
        slave: row.slave,
        tag: `airpanel-${userInfo?.id}`,
        cmd: "PG",
      };
      socket?.emit(OPERATE_MQTT, JSON.stringify(reqObj));
      toast.success(`Ping sent to slave ${row.slave}`);
    }
  };

  const handleCreate = () => {
    setGroupId(null);
    setSelectedSwitches([]);
    setBindingName("");
    setShowCreateCard(true);
  };

  const handleToggleClick = (binding: any) => {
    if (!selectedPremise?.id) {
      toast.error("Please select a premise.");
      return;
    }
    setToggleBindingData(binding);
    setToggleModalOpen(true);
  };

  const toggleActiveApiCall = async () => {
    if (!toggleBindingData || !selectedPremise?.id) return;

    const binding = toggleBindingData;
    const bindingGroupId = binding.groupId || binding._id;
    const switchIds = (binding.switches ?? [])
      .map((sw: any) => sw.switchId || sw._id)
      .filter(Boolean);
    const nextActive = !binding.isActive;

    setToggleLoading(true);
    setTogglingGroupId(bindingGroupId);
    try {
      await Request.put(`${url.BASE_URL}${url.DEVICE_BINDING_UPDATE}`, {
        groupId: bindingGroupId,
        userId,
        premiseId: selectedPremise.id,
        name: binding.name || "",
        switchIds,
        isActive: nextActive,
      });

      toast.success(
        nextActive
          ? "Binding activated successfully."
          : "Binding deactivated successfully.",
      );
      setToggleModalOpen(false);
      setToggleBindingData(null);
      setListReloadKey((k) => k + 1);
    } catch (error: any) {
      console.error("Error updating binding status:", error);
      const message =
        error.response?.data?.msg ||
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to update binding status";
      toast.error(message);
      if (error.response?.status === 401) {
        localStorage.removeItem("userInfo");
        localStorage.removeItem("selectedPremiseID");
        localStorage.removeItem("selectedUserID");
        localStorage.removeItem("selectedUserEmail");
        localStorage.removeItem("Authorization");
        localStorage.removeItem("permissions");
        window.location.reload();
      }
    } finally {
      setToggleLoading(false);
      setTogglingGroupId(null);
    }
  };

  const handleDeleteClick = (binding: any) => {
    if (!selectedPremise?.id) {
      toast.error("Please select a premise.");
      return;
    }
    setDeleteBindingData(binding);
    setDeleteModalOpen(true);
  };

  const deleteBindingApiCall = async () => {
    if (!deleteBindingData || !selectedPremise?.id) return;

    const bindingGroupId = deleteBindingData.groupId || deleteBindingData._id;
    setDeleteLoading(true);
    try {
      const reqObj = {
        groupId: bindingGroupId,
        userId,
        premiseId: selectedPremise.id,
      };

      await Request.delete(
        `${url.BASE_URL}${url.DEVICE_BINDING_DELETE}`,
        reqObj,
      );

      toast.success("Device binding deleted successfully.");
      setDeleteModalOpen(false);
      setDeleteBindingData(null);
      setListReloadKey((k) => k + 1);
    } catch (error: any) {
      console.error("Error deleting device binding:", error);
      const message =
        error.response?.data?.msg ||
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to delete device binding";
      toast.error(message);
      if (error.response?.status === 401) {
        localStorage.removeItem("userInfo");
        localStorage.removeItem("selectedPremiseID");
        localStorage.removeItem("selectedUserID");
        localStorage.removeItem("selectedUserEmail");
        localStorage.removeItem("Authorization");
        localStorage.removeItem("permissions");
        window.location.reload();
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleEditBinding = (binding: any) => {
    const switches = binding?.switches ?? [];
    setGroupId(binding.groupId || binding._id);
    setBindingName(binding.name || "");
    setSelectedSwitches(
      switches.map((sw: any) => ({
        button: sw.button,
        chipId: sw.chipId || "",
        slave: sw.slave || "",
        switchId: sw.switchId || sw._id,
        type: sw.type ?? 0,
      })),
    );
    setShowCreateCard(true);
  };

  const handleCloseCreateCard = () => {
    setShowCreateCard(false);
    setSelectedSwitches([]);
    setBindingName("");
    setGroupId(null);
    setExpandedSlaves(new Set());
    setSearchText("");
    setListPage(1);
  };

  const renderBindingListView = () => (
    <div
      className="d-flex flex-column flex-grow-1 w-100 p-2"
      style={{ minHeight: 0, overflow: "hidden", height: "100%" }}
    >
      <Card
        className="border shadow-sm mb-0 d-flex flex-column flex-grow-1 min-h-0"
        style={{ overflow: "hidden" }}
      >
        <CardBody
          className="p-0 d-flex flex-column flex-grow-1 min-h-0"
          style={{ overflow: "hidden" }}
        >
          <div className="p-3 border-bottom flex-shrink-0">
            <InputGroup size="sm">
              <Input
                type="search"
                placeholder="Search bindings..."
                value={listSearchText}
                onChange={(e) => setListSearchText(e.target.value)}
                className="border"
                style={{ fontSize: 13 }}
              />
            </InputGroup>
          </div>

          <div
            className="flex-grow-1 min-h-0 p-3"
            style={{
              overflowY: "auto",
              overflowX: "hidden",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {listLoading && <AirHomeLoader />}
            {!listLoading && bindingList.length === 0 && (
              <p className="text-muted small mb-0 text-center py-4">
                {listDebouncedSearch
                  ? "No bindings match your search."
                  : "No device bindings yet. Click Create to add one."}
              </p>
            )}
            {!listLoading && bindingList.length > 0 && (
              <div
                className="binding-card-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                  gap: "12px",
                  alignContent: "start",
                }}
              >
                {bindingList.map((binding: any) => {
                  const switches = binding?.switches ?? [];
                  const switchCount =
                    binding?.totalSwitches ?? switches.length ?? 0;
                  const bindingGroupId = binding.groupId || binding._id;
                  const isToggling = togglingGroupId === bindingGroupId;

                  return (
                    <Card
                      key={binding._id}
                      className="border shadow-sm h-100 mb-0"
                      style={{
                        minWidth: 280,
                        maxWidth: "100%",
                        width: "100%",
                      }}
                    >
                      <CardBody className="p-2 d-flex flex-column h-100 min-w-0">
                        <div className="d-flex justify-content-between align-items-start gap-2 mb-2 flex-shrink-0">
                          <div
                            className="min-width-0 flex-grow-1 pe-1"
                            style={{ minWidth: 0 }}
                          >
                            <div className="d-flex flex-row justify-content-between align-items-center gap-4">
                              <div
                                className="fw-bold text-truncate"
                                style={{ fontSize: 14, color: "#0f172a" }}
                                title={binding.name}
                              >
                                {binding.name || "Unnamed binding"}
                              </div>

                              <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                <div
                                  className="form-check form-switch mb-0 d-flex align-items-center gap-1"
                                  title={
                                    binding.isActive ? "Active" : "Inactive"
                                  }
                                >
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    id={`binding-active-${bindingGroupId}`}
                                    checked={!!binding.isActive}
                                    disabled={isToggling}
                                    onChange={() => handleToggleClick(binding)}
                                    style={{ cursor: "pointer" }}
                                  />
                                  <label
                                    className="form-check-label mb-0 text-muted"
                                    htmlFor={`binding-active-${bindingGroupId}`}
                                    style={{
                                      fontSize: 10,
                                      cursor: isToggling
                                        ? "not-allowed"
                                        : "pointer",
                                    }}
                                  >
                                    {/* {isToggling
                                      ? "..."
                                      : binding.isActive
                                        ? "Active"
                                        : "Inactive"} */}
                                  </label>
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-link p-0 text-dark"
                                  title="Edit"
                                  onClick={() => handleEditBinding(binding)}
                                >
                                  <i
                                    className="bx bx-edit-alt"
                                    style={{ fontSize: 18 }}
                                  />
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-link p-0 text-dark"
                                  title="Delete"
                                  onClick={() => handleDeleteClick(binding)}
                                >
                                  <i
                                    className="bx bx-trash"
                                    style={{ fontSize: 18 }}
                                  />
                                </button>
                              </div>
                            </div>
                            <div
                              className="text-muted mt-1"
                              style={{ fontSize: 11, lineHeight: 1.35 }}
                            >
                              {switchCount} switch
                              {switchCount === 1 ? "" : "es"} • Created{" "}
                              {formatBindingDate(binding.createdAt)}
                            </div>
                          </div>
                        </div>

                        {switches.length > 0 && (
                          <div
                            className="pt-2 border-top d-flex flex-column gap-1 mt-auto w-100"
                            style={{
                              minWidth: 0,
                              maxWidth: "100%",
                              minHeight: 180,
                              maxHeight: 180,
                              overflowY: "auto",
                              overflowX: "hidden",
                              WebkitOverflowScrolling: "touch",
                            }}
                          >
                            {switches.map((sw: any, index: number) => {
                              const typeStyle = getSwitchTypeStyle(
                                sw.type ?? 0,
                              );
                              return (
                                <div
                                  key={index}
                                  className="d-flex align-items-center gap-2 w-100 p-1"
                                >
                                  <button
                                    type="button"
                                    className="rounded d-flex align-items-center justify-content-center flex-shrink-0 border-0 p-0"
                                    style={{
                                      width: 24,
                                      height: 24,
                                      background: typeStyle.bg,
                                      color: typeStyle.color,
                                      cursor: sw.slave ? "pointer" : "default",
                                    }}
                                    title={
                                      sw.slave ? `Ping ${sw.slave}` : undefined
                                    }
                                    onClick={() =>
                                      handlePingClick({ slave: sw.slave })
                                    }
                                    disabled={!sw.slave}
                                  >
                                    <i
                                      className={`bx ${typeStyle.icon}`}
                                      style={{ fontSize: 12 }}
                                    />
                                  </button>
                                  <div className="flex-grow-1 min-width-0">
                                    <div
                                      className="text-truncate"
                                      style={{ fontSize: 11, fontWeight: 600 }}
                                    >
                                      {sw.slave || "-"}
                                    </div>
                                    <div
                                      className="text-muted text-truncate"
                                      style={{ fontSize: 10 }}
                                    >
                                      Switch {sw.button || "-"} •{" "}
                                      {getSwitchTypeLabel(sw.type ?? 0)}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </CardBody>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>

          {!listLoading && recordsFiltered > 0 && (
            <div
              className="d-flex align-items-center justify-content-between px-3 py-2 border-top flex-shrink-0 flex-wrap gap-2"
              style={{ fontSize: 12, backgroundColor: "#ffffff" }}
            >
              <span className="text-muted">
                Showing {listStartRow}-{listEndRow} of {recordsFiltered}
                {recordsTotal !== recordsFiltered && ` (total ${recordsTotal})`}
              </span>
              <div className="d-flex align-items-center gap-2">
                <select
                  className="form-select form-select-sm"
                  style={{ width: "auto", fontSize: 12 }}
                  value={listPerPage}
                  onChange={(e) => {
                    setListPerPage(Number(e.target.value));
                    setListPage(1);
                  }}
                >
                  {[5, 10, 25, 50].map((n) => (
                    <option key={n} value={n}>
                      {n} per page
                    </option>
                  ))}
                </select>
                <Button
                  color="light"
                  size="sm"
                  className="border"
                  disabled={listPage <= 1}
                  onClick={() => setListPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </Button>
                <span>
                  Page {listPage} of {listTotalPages}
                </span>
                <Button
                  color="light"
                  size="sm"
                  className="border"
                  disabled={listPage >= listTotalPages}
                  onClick={() =>
                    setListPage((p) => Math.min(listTotalPages, p + 1))
                  }
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );

  const handleSaveBinding = async () => {
    if (!bindingName.trim()) {
      toast.error("Please enter a binding name.");
      return;
    }
    if (selectedSwitches.length === 0) {
      toast.error("Please select at least two switch.");
      return;
    }
    if (!selectedPremise?.id) {
      toast.error("Please select a premise.");
      return;
    }

    setSaving(true);
    try {
      const reqObj = {
        ...(groupId ? { groupId } : {}),
        userId,
        premiseId: selectedPremise.id,
        name: bindingName.trim(),
        switchIds: selectedSwitches.map((s) => s.switchId),
      };

      if (groupId) {
        await Request.put(
          `${url.BASE_URL}${url.DEVICE_BINDING_UPDATE}`,
          reqObj,
        );
      } else {
        await Request.post(
          `${url.BASE_URL}${url.DEVICE_BINDING_CREATE}`,
          reqObj,
        );
      }

      toast.success(
        groupId
          ? "Device binding updated successfully."
          : "Device binding created successfully.",
      );
      handleCloseCreateCard();
      setListReloadKey((k) => k + 1);
    } catch (error: any) {
      console.error("Error saving device binding:", error);

      const message =
        error.response?.data?.msg?.message ||
        error.response?.data?.msg ||
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to create device binding";
      toast.error(message);
      if (error.response?.status === 401) {
        localStorage.removeItem("userInfo");
        localStorage.removeItem("selectedPremiseID");
        localStorage.removeItem("selectedUserID");
        localStorage.removeItem("selectedUserEmail");
        localStorage.removeItem("Authorization");
        localStorage.removeItem("permissions");
        window.location.reload();
      }
    } finally {
      setSaving(false);
    }
  };

  const renderDevicePanel = () => (
    <Card
      className="border shadow-sm mb-0 h-100 d-flex flex-column"
      style={{ minWidth: 0, flex: "1 1 50%", minHeight: 0, overflow: "hidden" }}
    >
      <CardBody
        className="p-0 d-flex flex-column flex-grow-1"
        style={{ minHeight: 0, overflow: "hidden" }}
      >
        <div className="p-3 border-bottom flex-shrink-0">
          <h6 className="fw-bold mb-2" style={{ color: "#0f172a" }}>
            1. Select Devices
          </h6>
          <InputGroup size="sm mb-2">
            <Input
              type="search"
              placeholder="Search device or switch..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="border"
              style={{ fontSize: 13 }}
            />
          </InputGroup>
          <Row className="g-1">
            {selectedRoomsNew.map((item: any, index: number) => (
              <Col
                key={index}
                lg={2}
                md={3}
                xs={6}
                className="d-flex align-items-center"
                style={{ fontSize: 14, cursor: "pointer" }}
                onClick={() => handleRoomChecked(item)}
              >
                <input
                  type="checkbox"
                  style={{ marginRight: 10, accentColor: "#343A40" }}
                  checked={item.isChecked}
                  readOnly
                />
                {item.name}
              </Col>
            ))}

            {/*Select All */}
            <Col
              lg={2}
              md={3}
              xs={6}
              className="d-flex align-items-center"
              style={{ fontSize: 14, cursor: "pointer" }}
              onClick={() => handleRoomAllChecked(true)}
            >
              <input
                type="checkbox"
                style={{ marginRight: 10, accentColor: "#343A40" }}
                checked={selectedRoomsNew.every((room: any) => room.isChecked)}
                readOnly
              />
              Select All
            </Col>

            {/* Remove All */}
            <Col
              lg={2}
              md={3}
              xs={6}
              className="d-flex align-items-center"
              style={{ fontSize: 14, cursor: "pointer" }}
              onClick={() => handleRoomAllChecked(false)}
            >
              <input
                type="checkbox"
                style={{ marginRight: 10, accentColor: "#343A40" }}
                checked={selectedRoomsNew.every((room: any) => !room.isChecked)}
                readOnly
              />
              Remove All
            </Col>
          </Row>
        </div>

        <div
          className="flex-grow-1 min-h-0 px-2 py-2"
          style={{
            overflowY: "auto",
            overflowX: "hidden",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {loading && <AirHomeLoader />}
          {!loading && filteredChips.length === 0 && (
            <p className="text-muted small px-2 mb-0">No devices found.</p>
          )}
          {!loading &&
            filteredChips.map((chip) => {
              const isExpanded = expandedSlaves.has(chip.slave);
              const switches = chip.switches || [];

              return (
                <Card
                  key={chip.id ?? chip.slave}
                  className="border shadow-sm mb-2 overflow-hidden"
                >
                  <button
                    type="button"
                    className="w-100 border-0 bg-white text-start p-2 d-flex align-items-center gap-4"
                    onClick={() => toggleExpand(chip.slave)}
                  >
                    <i
                      className={`bx ${
                        isExpanded ? "bx-chevron-down" : "bx-chevron-right"
                      }`}
                      style={{ fontSize: 18, color: "#64748b" }}
                    />
                    <i
                      className="bx bx-chip"
                      style={{ fontSize: 24, color: "#475569" }}
                    />
                    <div className="flex-grow-1 min-width-0">
                      <div
                        className="fw-semibold text-truncate"
                        style={{ fontSize: 14 }}
                      >
                        {chip.slave}
                      </div>

                      <div
                        className="text-muted d-flex align-items-center gap-4 text-truncate"
                        style={{ fontSize: 12 }}
                      >
                        <span>
                          Room : <strong>{chip.roomName || "-"}</strong>
                        </span>
                        <span>
                          Type : <strong>{chip.modelType || "-"}</strong>
                        </span>
                      </div>
                    </div>
                    {chip.isOnline ? (
                      <span className="font-size-11 badge-soft-success badge bg-secondary">
                        online
                      </span>
                    ) : (
                      <span className="font-size-11 badge-soft-danger badge bg-secondary">
                        offline
                      </span>
                    )}
                  </button>

                  {isExpanded && (
                    <CardBody className="p-0 border-top">
                      {switches.length === 0 ? (
                        <p className="text-muted small p-2 mb-0">
                          No switches.
                        </p>
                      ) : (
                        switches.map((sw: any) => {
                          const typeStyle = getSwitchTypeStyle(sw.type ?? 0);
                          const selected = isSwitchSelected(chip.slave, sw._id);
                          // const is2WayBind = !!sw.is2WayBind;
                          const is2WayBind = false;

                          return (
                            <div
                              key={sw._id}
                              className="d-flex align-items-center gap-2 px-2 py-2 border-bottom bg-white"
                              style={is2WayBind ? { opacity: 0.55 } : undefined}
                              title={
                                is2WayBind
                                  ? "This switch is already in another binding group"
                                  : undefined
                              }
                            >
                              <div
                                className="d-flex align-items-center justify-content-center rounded flex-shrink-0"
                                style={{
                                  width: 32,
                                  height: 32,
                                  background: typeStyle.bg,
                                  color: typeStyle.color,
                                }}
                              >
                                <i
                                  className={`bx ${typeStyle.icon}`}
                                  style={{ fontSize: 18 }}
                                />
                              </div>
                              <div className="flex-grow-1 min-width-0">
                                <div
                                  className="fw-medium"
                                  style={{ fontSize: 13 }}
                                >
                                  {sw.displayName || `Switch ${sw.button}`}
                                </div>
                                <div
                                  className="text-muted"
                                  style={{ fontSize: 11 }}
                                >
                                  Button {sw.button} •{" "}
                                  {getSwitchTypeLabel(sw.type ?? 0)}
                                </div>
                              </div>
                              {is2WayBind ? (
                                <span
                                  className="badge bg-secondary flex-shrink-0"
                                  style={{ fontSize: 10 }}
                                >
                                  In group
                                </span>
                              ) : selected ? (
                                <Button
                                  color="success"
                                  size="sm"
                                  outline
                                  className="flex-shrink-0"
                                  style={{ fontSize: 12, padding: "2px 10px" }}
                                  onClick={() =>
                                    removeSwitch(chip.slave, sw._id)
                                  }
                                >
                                  Added
                                </Button>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn-link flex-shrink-0 p-0 text-primary fw-semibold"
                                  style={{
                                    fontSize: 13,
                                    textDecoration: "none",
                                  }}
                                  onClick={() => addSwitch(chip, sw)}
                                >
                                  + Add
                                </button>
                              )}
                            </div>
                          );
                        })
                      )}
                    </CardBody>
                  )}
                </Card>
              );
            })}
        </div>
      </CardBody>
    </Card>
  );

  const renderBindingPanel = () => (
    <Card
      className="border shadow-sm mb-0 h-100 d-flex flex-column"
      style={{ minWidth: 0, flex: "1 1 50%", minHeight: 0, overflow: "hidden" }}
    >
      <CardBody
        className="p-0 d-flex flex-column flex-grow-1"
        style={{ minHeight: 0, overflow: "hidden" }}
      >
        <div className="p-3 border-bottom flex-shrink-0">
          <h6 className="fw-bold mb-3" style={{ color: "#0f172a" }}>
            2. Binding Details
          </h6>
          <label className="form-label small fw-semibold mb-1">
            Binding Name <span className="text-danger">*</span>
          </label>
          <Input
            type="text"
            placeholder="e.g. Daughter Down Light"
            value={bindingName}
            maxLength={BINDING_NAME_MAX}
            onChange={(e) => setBindingName(e.target.value)}
            className="border"
            style={{ fontSize: 14 }}
          />
        </div>

        <div
          className="flex-grow-1 min-h-0 px-3 py-2"
          style={{
            overflowY: "auto",
            overflowX: "hidden",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="fw-semibold" style={{ fontSize: 14 }}>
              Selected Switches ({selectedSwitches.length})
            </span>
            {selectedSwitches.length > 0 && (
              <button
                type="button"
                className="btn btn-link p-0 text-danger"
                style={{ fontSize: 12 }}
                onClick={clearAllSelected}
              >
                Clear All
              </button>
            )}
          </div>

          {selectedSwitches.length === 0 ? (
            <p className="text-muted small">No switches selected yet.</p>
          ) : (
            selectedSwitches.map((sw) => {
              const typeStyle = getSwitchTypeStyle(sw.type);
              return (
                <Card
                  key={switchKey(sw.slave, sw.switchId)}
                  className="border shadow-sm mb-2"
                >
                  <CardBody className="p-2 d-flex align-items-center gap-2">
                    <div
                      className="rounded d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        width: 40,
                        height: 40,
                        background: typeStyle.bg,
                        color: typeStyle.color,
                      }}
                    >
                      <i
                        className={`bx ${typeStyle.icon}`}
                        style={{ fontSize: 22 }}
                      />
                    </div>
                    <div className="flex-grow-1 min-width-0">
                      <div
                        className="fw-semibold text-truncate"
                        style={{ fontSize: 14 }}
                      >
                        {sw.displayName}
                      </div>
                      <div className="text-muted" style={{ fontSize: 11 }}>
                        Slave: {sw.slave} • Button: {sw.button}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-link text-danger p-0 flex-shrink-0"
                      onClick={() => removeSwitch(sw.slave, sw.switchId)}
                      aria-label="Remove"
                    >
                      <i className="bx bx-trash" style={{ fontSize: 20 }} />
                    </button>
                  </CardBody>
                </Card>
              );
            })
          )}
        </div>
      </CardBody>
    </Card>
  );

  return (
    <>
      {isDeleteModalOpen && (
        <ConfirmationModal
          headerTitle="Delete Binding?"
          text={`Are you sure you want to delete "${
            deleteBindingData?.name || "Unnamed binding"
          }"?`}
          buttonText="Delete"
          onClick={deleteBindingApiCall}
          cancleBtnClick={() => {
            setDeleteModalOpen(false);
            setDeleteBindingData(null);
          }}
          loading={deleteLoading}
        />
      )}
      {isToggleModalOpen && toggleBindingData && (
        <ConfirmationModal
          headerTitle={
            toggleBindingData.isActive
              ? "Deactivate Binding?"
              : "Activate Binding?"
          }
          text={`Are you sure you want to ${
            toggleBindingData.isActive ? "deactivate" : "activate"
          } "${toggleBindingData.name || "Unnamed binding"}"?`}
          buttonText={toggleBindingData.isActive ? "Deactivate" : "Activate"}
          onClick={toggleActiveApiCall}
          cancleBtnClick={() => {
            setToggleModalOpen(false);
            setToggleBindingData(null);
          }}
          loading={toggleLoading}
        />
      )}
      <Offcanvas
        isOpen={offcanvasOpen}
        toggle={closeOffcanvas}
        direction="end"
        style={{
          width: "90%",
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          maxHeight: "100vh",
        }}
        className="nodrag nopan"
      >
        <OffcanvasHeader
          toggle={closeOffcanvas}
          className="d-flex align-items-center justify-content-between w-100"
        >
          <div className="d-flex align-items-center justify-content-between w-100 pe-3 gap-2">
            <h5 className="modal-title mb-0" style={{ fontWeight: 800 }}>
              Device Binding
            </h5>
            {!showCreateCard && (
              <Button color="dark" size="sm" onClick={handleCreate}>
                Create
              </Button>
            )}
          </div>
        </OffcanvasHeader>
        <OffcanvasBody
          className="d-flex flex-column p-0 flex-grow-1 min-h-0"
          style={{
            overflow: "hidden",
            backgroundColor: "#f7f7fa",
            minHeight: 0,
            flex: "1 1 auto",
            height: "calc(100vh - 57px)",
          }}
        >
          {showCreateCard ? (
            <div
              className="d-flex flex-column flex-grow-1 min-h-0"
              style={{ overflow: "hidden", height: "100%" }}
            >
              <div
                className="d-flex flex-grow-1 gap-2 px-2 py-2 min-h-0"
                style={{ overflow: "hidden", flex: "1 1 auto", minHeight: 0 }}
              >
                {renderDevicePanel()}
                {renderBindingPanel()}
              </div>
              <div className="flex-shrink-0 d-flex justify-content-end gap-2 px-3 py-3 border-top bg-white">
                <Button
                  color="light"
                  // className="border"
                  size="sm"
                  onClick={handleCloseCreateCard}
                >
                  Cancel
                </Button>
                <Button
                  color="dark"
                  size="sm"
                  onClick={handleSaveBinding}
                  disabled={saving}
                >
                  {saving ? "Saving..." : groupId ? "Update" : "Save"}
                </Button>
              </div>
            </div>
          ) : (
            <div
              className="d-flex flex-column flex-grow-1 min-h-0 w-100"
              style={{ overflow: "hidden", height: "100%" }}
            >
              {renderBindingListView()}
            </div>
          )}
        </OffcanvasBody>
      </Offcanvas>
    </>
  );
}

export default DeviceBinding;
